import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/index';
import { environment } from '../../../environments/environment';
import { LoginResponseType } from '../../oauth/constant/login-response-type';
import { OAuthConstants } from '../../oauth/constant/oAuth.constants';
import { CallbackDomainResponse } from '../models/CallbackDomainResponse';
import { LoginPageResponse } from '../../oauth/models/headless-cms/login-page-response';
import { LoginForm } from '../../oauth/models/login/LoginForm';
import { LoginResponse } from '../../oauth/models/login/LoginResponse';
import { MaintenanceWindowNotice } from '../../portal/models/maintenanceWindowNotice';
import { PasswordPolicyResponse } from '../../oauth/models/reset-password/PasswordPolicyResponse';
import { ResetForm } from '../../oauth/models/reset-password/ResetForm';
import { LoginAds } from '../../oauth/models/headless-cms/LoginAds';
import { UtilsService } from './utils';
import { CookieService } from 'ngx-cookie-service';
import { OpenidOauthConfig } from '../../oauth/models/login/OpenidOauthConfig';
import { LogoutPage } from '../../oauth/models/logout/LogoutPage';
import { verifyOtpRequest, verifyOtpResponse } from '../../oauth/models/reset-password/ForgotPassword';
import { OtpValidateForm } from '../../oauth/models/mfa/OtpValidateForm';
import { OtpForm } from '../../oauth/models/mfa/OtpForm';
import { OtpResponse } from '../../oauth/models/mfa/OtpResponse';
import {isEmpty, isNull} from 'lodash-es';

@Injectable()
export class LoginService {
  private readonly loginApi = '/uas/v1/login';
  private readonly initiateLoginApi = '/uas/v1/initiate-login';
  private readonly callBackApi = '/uas/v1/initiate/callback';
  private readonly sendEmailApi = '/uas/v1/user/email-password';
  private readonly resetPasswordApi = '/uas/v1/ott/reset-password';
  private readonly getValidTokenApi = '/uas/v1/ott/validate-ott';
  private readonly passwordPolicyApi = '/uas/v1/ott/password-policy';
  private readonly loginAdsApi = '/uas/v1/cms/login-ads';
  private readonly assetApi = '/uas/v1/cms/asset/{id}';
  private readonly resendPwdLinkApi = '/uas/v1/ott/send-reset-link/{guid}';
  private readonly msgProperitesApi = '/uas/v1/login-page';
  private readonly tokenStateApi = '/uas/v1/token/{token}/state';
  private readonly maintenanceNoticeApi = '/uas/v1/maintenance-window/{status}';
  private readonly openIdLoginApi = '/uas/v1/openid/login/{provider}?token={token}';
  private readonly oauthSettingsApi = '/uas/v1/oauth-settings/{provider}';
  private readonly changePasswordApi = '/uas/v1/user/change-password';
  private readonly logoutPageApi = '/uas/v1/logout-page';
  private readonly addSessionCookieApi = '/uas/v1/session-cookie';
  private readonly verifyOtpApi = '/uas/v1/user/otp-verify';
  private readonly mfaOtpVerifyApi = '/uas/v1/mfa/otp/validate';
  private readonly mfaOtpResendApi = '/uas/v1/mfa/otp/send';

  private baseUrl: string;

  constructor(private http: HttpClient,
              private utils: UtilsService,
              private cookieService: CookieService) {
    this.baseUrl = environment.backendHost;
    if (this.baseUrl.endsWith('/')) {
      if (isNull(this.baseUrl)) {
        return;
      }
      this.baseUrl = this.baseUrl.substr(0, this.baseUrl.length - 1);
    }
  }

  public getUrl(path: String) {
    return this.baseUrl + path;
 }

  public getChallengeConfigHeader(challenge) {
    const httpOptions = {
      headers: new HttpHeaders({
        'X-OAUTH-CHALLENGE':  challenge,
      })
    };
    return httpOptions;
  }


  public getDomainConfigHeader(domain) {
    const httpOptions = {
      headers: new HttpHeaders({
        'X-GREYTIP-DOMAIN': domain,
      })
    };
    return httpOptions;
  }

  public getAccessTokenConfigHeader(token) {
    const httpOptions = {
      headers: new HttpHeaders({
        'ACCESS-TOKEN': token,
      })
    };
    return httpOptions;
  }

  public login(form: LoginForm, challenge: string): Observable<LoginResponse> {
    const data = {
      userName: form.username,
      password: form.password
    };
    const url = this.getUrl(this.loginApi);
    return this.http.post<LoginResponse>(url, data, this.getChallengeConfigHeader(challenge));
  }

  public initiateLogin(challenge: String): Observable<LoginResponse> {
    const url = this.getUrl(`${this.initiateLoginApi}/${challenge}`);
    return this.http.get<LoginResponse>(url, this.getChallengeConfigHeader(challenge));
  }

  public isActiveToken(token: String): Observable<boolean> {
    let url = this.utils.parseTemplateString(this.tokenStateApi, {token});
    url = this.getUrl(url);
    return this.http.get<boolean>(url);
  }

  public isRedirect(loginResponse: LoginResponse): boolean {
    return loginResponse.loginType === LoginResponseType.REDIRECT;
  }

  public initiateCallback(token: string): Observable<CallbackDomainResponse> {
    const url = this.getUrl(`${this.callBackApi}`);
    const requestOptions = this.getAccessTokenConfigHeader(token);
    return this.http.post<CallbackDomainResponse>(url, {}, requestOptions);
  }

  public sendResetLink(resetForm: ResetForm, challenge: string): Observable<any> {
    const data = {
      userNameOrEmail: resetForm.username,
      resetByMobile: resetForm.resetByMobile
    };
    const url = this.getUrl(`${this.sendEmailApi}`);
    return this.http.post(url, data, this.getChallengeConfigHeader(challenge));
  }

  public resetPassword(resetPwdForm, domain): Observable<any> {
    const url = this.getUrl(`${this.resetPasswordApi}`);
    const data = {
      guid: resetPwdForm.guid,
      newPassword: resetPwdForm.encNewPassword,
      confirmPassword: resetPwdForm.encConfirmPassword
    };
    return this.http.post(url, data, this.getDomainConfigHeader(domain));
  }

  public validateToken(guid: string, domain: string): Observable<any> {
    const url = this.getUrl(`${this.getValidTokenApi}/${guid}`);
    return this.http.get(url, this.getDomainConfigHeader(domain));
  }

  public getPasswordPolicy(domain): Observable<PasswordPolicyResponse> {
    const url = this.getUrl(this.passwordPolicyApi);
    return this.http.get<PasswordPolicyResponse>(url, this.getDomainConfigHeader(domain));
  }

  public getLoginAds(challenge: string, domain: string): Observable<LoginAds[]> {
    const header = challenge ? this.getChallengeConfigHeader(challenge) : this.getDomainConfigHeader(domain);
    const url = this.getUrl(this.loginAdsApi);
    return this.http.get<LoginAds[]>(url, header);
  }

  public getAsset(id): string {
    let url = this.utils.parseTemplateString(this.assetApi, {id});
    url = this.getUrl(url);
    return url;
  }

  public resendPwdLink(guid, domain) {
    let url = this.utils.parseTemplateString(this.resendPwdLinkApi, {guid});
    url = this.getUrl(url);
    return this.http.get(url, this.getDomainConfigHeader(domain));
  }

  public getLoginPageResponse(challenge: string, domain: string): Observable<LoginPageResponse> {
    const url = this.getUrl(this.msgProperitesApi);
    const header = challenge ? this.getChallengeConfigHeader(challenge) : this.getDomainConfigHeader(domain);
    return this.http.get<LoginPageResponse>(url, header);
  }

  public getLogoutPageResposne(domain: string): Observable<LogoutPage> {
    const url = this.getUrl(this.logoutPageApi);
    return  this.http.get<LogoutPage>(url, this.getDomainConfigHeader(domain));
  }

  public getMaintenanceWindowNotes(challenge: string, status: string): Observable<MaintenanceWindowNotice> {
   let url = this.utils.parseTemplateString(this.maintenanceNoticeApi, {status});
   url = this.getUrl(url);
   return this.http.get<MaintenanceWindowNotice>(url, this.getChallengeConfigHeader(challenge));
  }

  public removeTokenForGuidLogin(requestedHref: string): void {
    const accessToken = this.cookieService.get(OAuthConstants.ACCESS_TOKEN);
    const urlParams = new URLSearchParams(window.location.search);
    const guidExists = urlParams.has('guid') ? !isEmpty(urlParams.get('guid')) : false;
    if (guidExists && accessToken) {
      this.cookieService.delete(OAuthConstants.ACCESS_TOKEN, '/', window.location.host);
    }
  }

  public openIdLogin(challenge: string, provider: string, token: string): Observable<LoginResponse> {
    let url = this.utils.parseTemplateString(this.openIdLoginApi, {provider, token});
    url = this.getUrl(url);
    return this.http.get<LoginResponse>(url, this.getChallengeConfigHeader(challenge));
  }

  public getOAuthSettings(challenge: string, provider: string): Observable<OpenidOauthConfig> {
    let url = this.utils.parseTemplateString(this.oauthSettingsApi, {provider});
    url = this.getUrl(url);
    return this.http.get<OpenidOauthConfig>(url, this.getChallengeConfigHeader(challenge));
  }

  public changePassword(changePwdForm): Observable<any> {
    const accessToken = this.cookieService.get(OAuthConstants.ACCESS_TOKEN);
    const url = this.getUrl(`${this.changePasswordApi}`);
    const data = {
      currentPassword: changePwdForm.encCurrentPassword,
      newPassword: changePwdForm.encNewPassword,
      confirmPassword: changePwdForm.encConfirmPassword
    };
    return this.http.post(url, data, this.getAccessTokenConfigHeader(accessToken));
  }

  public addSessionCookie(token: string): Observable<any> {
    const url = this.getUrl(`${this.addSessionCookieApi}`);
    const requestOptions = this.getAccessTokenConfigHeader(token);
    return this.http.post<any>(url, {}, requestOptions);
  }

  public verifyOtp(data: verifyOtpRequest, challenge: string): Observable<verifyOtpResponse> {
    const url = this.getUrl(this.verifyOtpApi);
    return this.http.post<verifyOtpResponse>(url, data, this.getChallengeConfigHeader(challenge));
}
public mfaOtpValidate(data: OtpValidateForm, challenge: string): Observable<LoginResponse> {
  const url = this.getUrl(this.mfaOtpVerifyApi);
  return this.http.post<LoginResponse>(url, data, this.getChallengeConfigHeader(challenge));
}

public mfaOtpResend(data: OtpForm, challenge: string): Observable<OtpResponse> {
  const url = this.getUrl(this.mfaOtpResendApi);
  return this.http.post<OtpResponse>(url, data, this.getChallengeConfigHeader(challenge));
}
}
